import { useState, useCallback } from "react";

/**
 * Custom hook to manage the state and actions for a row editing modal.
 * @param {Object} defaultData - Default data to populate the form when a new item is effectively being created or to merge with existing row data.
 * @returns {Object} An object containing:
 *  - isModalOpen: boolean - Whether the modal is currently open.
 *  - editingRow: Object | null - The data of the row currently being edited, or null if no row is being edited.
 *  - openModal: Function - A function to open the modal and set the editing row data. Expects rowData as an argument.
 *  - closeModal: Function - A function to close the modal and clear the editing row data.
 */
export const useRowEditingModal = (defaultData = {}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const openModal = useCallback(
    (rowData) => {
      // When opening, merge the passed rowData with defaultData.
      // This ensures that if rowData is missing some fields defined in defaultData,
      // they are still present in the editingRow state.
      setEditingRow({ ...defaultData, ...rowData });
      setIsModalOpen(true);
    },
    [defaultData]
  ); // defaultData should be stable, but include if it could change

  const closeModal = useCallback(() => {
    setEditingRow(null); // Clear the editing data
    setIsModalOpen(false);
  }, []);

  const startEditingRow = useCallback(
    (rowData) => {
      openModal(rowData.original || rowData);
    },
    [openModal]
  );

  return {
    isModalOpen,
    editingRow,
    startEditingRow,
    closeModal,
  };
};
